import { PEPVIGIL } from 'pages/dlops-dashboard/utils/constant';
import {
    Day2CatalogDefinitionId,
    ServiceAccountDefinitionId,
    nsgCatalogDefinitionId,
    ProjectOnboardCatalogDefinitionId,
    GithubOnboardingCatalogDefinitionId,
    SsoOktaCatalogDefinitionId,
    SsoOktaModifyCatalogDefinitionId,
    // CloudOnboardingCatalogDefinitionID,
} from './catalogDefinitionId';
import { SCOPE_ORDER_MGMT_WRITE } from './scope';
import { getAnnouncementRestrictions, isAnnouncementRestricted } from './announcementRestrictions';

/**
 * Returns [SCOPE_ORDER_MGMT_WRITE] if any of the given catalog codes are restricted, otherwise [].
 */
const getScopeForCatalogs = (...catalogCodes) => {
    if (catalogCodes.some((code) => isAnnouncementRestricted(code))) {
        return [SCOPE_ORDER_MGMT_WRITE];
    }
    return [];
};

/**
 * Returns [SCOPE_ORDER_MGMT_WRITE] only if ALL of the given catalog codes are restricted, otherwise [].
 * Used for parent menu items that should remain visible if at least one child is accessible.
 */
const getParentScopeForCatalogs = (...catalogCodes) => {
    if (catalogCodes.every((code) => isAnnouncementRestricted(code))) {
        return [SCOPE_ORDER_MGMT_WRITE];
    }
    return [];
};

/**
 * Returns the menu items with dynamic scopes based on announcement restrictions.
 * When all catalogs are restricted, certain menu items require SCOPE_ORDER_MGMT_WRITE (admin only).
 * When specific catalogs are restricted, only the related menu items require SCOPE_ORDER_MGMT_WRITE.
 */
// eslint-disable-next-line
export const getMenuItems = () => {
    const { allCatalogsRestricted } = getAnnouncementRestrictions();
    const restrictedScope = allCatalogsRestricted ? [SCOPE_ORDER_MGMT_WRITE] : [];

    const nsgScope = getScopeForCatalogs(nsgCatalogDefinitionId[0]);
    const projectOnboardingScope = getScopeForCatalogs(ProjectOnboardCatalogDefinitionId[0]);
    const githubScope = getScopeForCatalogs(GithubOnboardingCatalogDefinitionId);
    const serviceAccountScope = getScopeForCatalogs(ServiceAccountDefinitionId[0]);
    const day2Scope = getScopeForCatalogs(Day2CatalogDefinitionId[0]);
    const ssoOnboardingScope = getScopeForCatalogs(SsoOktaCatalogDefinitionId[0]);
    const ssoModificationScope = getScopeForCatalogs(SsoOktaModifyCatalogDefinitionId[0]);
    const ssoScope = getParentScopeForCatalogs(SsoOktaCatalogDefinitionId[0], SsoOktaModifyCatalogDefinitionId[0]);
    const accessMgmtScope = getParentScopeForCatalogs(Day2CatalogDefinitionId[0], SsoOktaCatalogDefinitionId[0], SsoOktaModifyCatalogDefinitionId[0]);

    return [
        {
            id: 'riServices',
            name: 'Solutions',
            allowedScopes: [],
            link: '/reference-implementation-services',
        },
        {
            id: 'services',
            name: 'Services',
            allowedScopes: [],
            link: '/services?services=aws',
        },
        // {
        //     id: 'coludOnboarding',
        //     name: 'Cloud Onboarding',
        //     allowedScopes: [],
        //     link: `/cloud-adoption/${CloudOnboardingCatalogDefinitionID[0]}`,
        // },
        {
            id: 'orders',
            name: 'Orders',
            allowedScopes: [],
            link: '/orders',
        },
        {
            id: 'approvals',
            name: 'Approvals',
            allowedScopes: [],
            link: '/approvals',
        },
        {
            id: 'More',
            name: 'More',
            allowedScopes: [],
            children: [
                {
                    id: 'appProfiles',
                    name: 'App Profiles',
                    allowedScopes: [],
                    link: '/app-profile',
                },
                {
                    id: 'Dashboard',
                    name: 'Dashboard',
                    allowedScopes: [SCOPE_ORDER_MGMT_WRITE],
                    link: '/dashboard',
                },
                {
                    id: 'azure-sku',
                    name: 'Azure Resources',
                    allowedScopes: [SCOPE_ORDER_MGMT_WRITE],
                    link: '/sku',
                },
                {
                    id: 'azure-nsg',
                    name: 'NSG Automation',
                    allowedScopes: nsgScope.length ? nsgScope : [SCOPE_ORDER_MGMT_WRITE, 'nsg.write'],
                    link: `/nsg-common-rule/${nsgCatalogDefinitionId[0]}`,
                },
                {
                    id: 'inventoryReport',
                    name: 'Inventory Report',
                    allowedScopes: [],
                    link: `/inventory-list`,
                },
                // {
                //     id: 'securityTool',
                //     name: 'Application Security Tool',
                //     allowedScopes: [],
                //     link: `/security-tool`,
                // },
                {
                    id: 'projectOnboarding',
                    name: 'Project Onboarding',
                    allowedScopes: projectOnboardingScope,
                    link: `/project-onboarding/${ProjectOnboardCatalogDefinitionId[0]}`,
                },
                {
                    id: 'githubOnboarding',
                    name: 'GitHub Enterprise Onboarding',
                    allowedScopes: githubScope,
                    link: `/github-onboarding/${GithubOnboardingCatalogDefinitionId}`,
                },
                {
                    id: 'service-account-creation',
                    name: 'Service Account(AD/LDAP)',
                    allowedScopes: serviceAccountScope,
                    link: `/service-account-creation/${ServiceAccountDefinitionId[0]}`,
                },
                // {
                //     id: 'service-account-ci',
                //     name: 'Service Account(SQL)',
                //     allowedScopes: [],
                //     link: `/service-account-ci/${ServiceAccountDefinitionId[0]}`,
                // },
                {
                    id: 'onboard-application-service',
                    name: 'Onboard Application Service',
                    allowedScopes: restrictedScope,
                    link: `/onboard-application-service`,
                },
                // {
                //     id: 'mdt-onboarding',
                //     name: 'MDT Onboarding',
                //     allowedScopes: [],
                //     link: `/mdt-onboarding`,
                // },
                // {
                //     id: 'day-2-operation',
                //     name: 'Day2 Operation',
                //     allowedScopes: [],
                //     children: [
                //         {
                //             id: 'iaas-day-2-operation',
                //             name: 'IaaS',
                //             allowedScopes: [],
                //             link: `/day2-operation/AZWINVM2016`,
                //         },
                //         {
                //             id: 'paas-day-2-operation',
                //             name: 'PaaS',
                //             allowedScopes: [],
                //             link: `/day2-operation-paas/AZWINVM2016`,
                //         },
                //     ],
                // },
                {
                    id: 'access-management',
                    name: 'Access Management',
                    allowedScopes: accessMgmtScope,
                    children: [
                        {
                            id: 'adding-secondary-id',
                            name: 'LDAP & Active Directory Group Membership',
                            allowedScopes: day2Scope,
                            link: `/access-management-myidm/${Day2CatalogDefinitionId[0]}`,
                        },
                        {
                            id: 'onboard-developers',
                            name: 'Onboarding Developers',
                            allowedScopes: restrictedScope,
                            link: `/access-management-onboard-developers`,
                        },
                        {
                            id: 'ssoOkta',
                            name: 'SSO Integration',
                            allowedScopes: ssoScope,
                            children: [
                                {
                                    id: 'ssoOkta-application-onboarding',
                                    name: 'SSO Onboarding',
                                    allowedScopes: ssoOnboardingScope,
                                    link: `/sso-okta-onboarding/${SsoOktaCatalogDefinitionId[0]}`,
                                },
                                {
                                    id: 'ssoOkta-application-modification',
                                    name: 'SSO Modification',
                                    allowedScopes: ssoModificationScope,
                                    link: `/sso-okta-modification/${SsoOktaModifyCatalogDefinitionId[0]}`,
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'pepvigil',
                    name: `${PEPVIGIL} Dashboard`,
                    allowedScopes: [],
                    children: [
                        // {
                        //     id: 'pepvigil-Overview',
                        //     name: `${PEPVIGIL} Overview Dashboard`,
                        //     allowedScopes: [],
                        //     link: `/pepvigil-overview-dashboard`,
                        // },
                        {
                            id: 'pepvigil-Projects',
                            name: `${PEPVIGIL} Projects`,
                            allowedScopes: [],
                            link: `/pepvigil-projects`,
                        },
                    ],
                },
            ],
        },
        {
            id: 'mypersona',
            name: 'My Persona',
            allowedScopes: [],
            children: [
                {
                    id: 'signout',
                    name: 'Sign Out',
                    allowedScopes: [],
                },
            ],
        },
    ];
};

// Keep backward-compatible static export for any other consumers
// eslint-disable-next-line
export const menuItems = getMenuItems();
